import moment from "moment"

const toggleMessagingAccess = async ({
  organizationId,
  enableMessaging,
  totalAmount,
  messagingFee,
  employeeRangeQty,
  firebase,
  paymentChargeUrl,
  addToast,
}) => {
  try {
    const now = new Date()

    if (!enableMessaging) {
      await firebase.updateOrgData({
        orgId: organizationId,
        orgData: {
          hasMessagingAccess: false,
          messagingNextPaymentDay: null,
        },
      })

      await firebase.updateMessagingAccessForOrgUsers({
        organizationId,
        access: false,
      })

      addToast("Mensajería desactivada con éxito", { appearance: "info" })
      return { success: true, status: "deactivated" }
    }

    const orgSnap = await firebase.org(organizationId).get()
    const getOrgTokens = await firebase.getOrgTokens({ orgId: organizationId })
    const validToken = getOrgTokens.find(
      (t) => t.token?.startsWith("card_") || t.token?.startsWith("pm_")
    )

    const org = orgSnap.data()

    const paymentToken = org?.stripeUserId && validToken?.token

    if (!paymentToken) throw new Error("No valid payment method available")

    const addressSnapshot = await firebase.getOrgAddress(organizationId)
    let orgAddress = null

    if (!addressSnapshot.empty) {
      addressSnapshot.forEach((d) => {
        const addressData = d.data()
        orgAddress = {
          city: addressData.address_city,
          country: addressData.address_country,
          line1: addressData.address_line1,
          line2: addressData.address_line2,
          state: addressData.address_state,
          zip: addressData.address_zip,
        }
      })
    }

    const chargePayload = {
      client: {
        email: org.email,
        customerId: org.stripeUserId,
      },
      charge: {
        amount: totalAmount,
        currency: "usd",
        description: `Mensajería push para una banda de ${employeeRangeQty} colaboradores`,
        paymentType: "card",
      },
      token: paymentToken,
      metadata: [
        ["messagingAccess", true],
        ["collaboratorCount", employeeRangeQty],
      ],
      isDirectCharge: true,
      transactionType: "MessagingAccess",
    }

    const token = await firebase.getIdToken()

    const res = await fetch(paymentChargeUrl, {
      method: "POST",
      body: JSON.stringify(chargePayload),
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })

    if (!res.ok) throw new Error("Payment failed")

    const parsedRes = await res.json()

    const transaction = await firebase.getTransaction(parsedRes.transactionId)
    const transactionData = transaction.data()

    const s = await firebase.getInvoiceConsecutive({ country: org.country })
    const consecutive =
      org.country.toUpperCase() +
      now.getFullYear().toString().slice(-2) +
      (now.getMonth() + 1).toString().padStart(2, "0") +
      s.toString().padStart(4, "0")

    const invoice = {
      customerId: organizationId,
      customerName: org.businessName,
      customerType: org.type,
      createdAt: Date.now(),
      email: org.email,
      paidAmount: totalAmount,
      subTotal: totalAmount,
      taxes: 0,
      total: totalAmount,
      balance: 0,
      type: "receipt",
      address: orgAddress,
    }

    const invoiceItems = [
      {
        amount: messagingFee,
        description: `Messaging access fee for ${employeeRangeQty} collaborators range`,
        qty: employeeRangeQty,
        totalAmount,
      },
    ]

    const invoiceId = await firebase.saveInvoice({
      invoiceData: {
        ...invoice,
        invoiceNumber: consecutive,
        transactionId: parsedRes.transactionId,
        currency: transactionData?.currency,
        cardBrand: transactionData?.payment_method_details?.card?.brand || null,
        last4: transactionData?.payment_method_details?.card?.last4 || null,
      },
    })

    const nextPaymentDateToCharge = moment(now)
      .add(1, "months")
      .startOf("day")
      .valueOf()

    await firebase.saveInvoiceItems({
      invoiceId,
      invoiceItems,
    })

    await firebase.updateOrgData({
      orgId: organizationId,
      orgData: {
        hasMessagingAccess: true,
        messagingNextPaymentDay: nextPaymentDateToCharge,
        employeeRangeQty,
      },
    })

    await firebase.updateMessagingAccessForOrgUsers({
      organizationId,
      access: true,
    })

    addToast("Mensajería activada con éxito", { appearance: "success" })

    return { success: true, status: "activated" }
  } catch (error) {
    const msg =
      error.message === "Payment failed"
        ? "El pago ha fallado. Por favor, intenta nuevamente."
        : "Error al activar mensajería."

    console.error("Error activating messaging:", error)

    await firebase.updateOrgData({
      orgId: organizationId,
      orgData: {
        errorData: {
          message: error.message,
          code: error.code || null,
          stack: error.stack,
          timestamp: new Date().toISOString(),
        },
      },
    })

    addToast(msg, { appearance: "error" })

    return { success: false, error: msg }
  }
}

export default toggleMessagingAccess
